/**
 * 自分のコードを測るモードのプリセットと設定（complexity-meter.md §2, §3）。
 */
import {
  DEFAULT_SIZES,
  opsIdentifiers,
  OpsError,
  type MeterInput,
  type MeterSpec,
} from '../../lib/complexity';
import { MAX_CASES } from '../../lib/sandboxd';

/** カードから開いたときの初期値（card-schema.md §3.7 の meter）。 */
export interface OwnInitial {
  code: string;
  input: MeterInput;
  sizes: readonly number[];
  ops?: string | undefined;
}

export interface OwnPreset {
  id: string;
  label: string;
  code: string;
  ops: string;
  input: MeterInput;
}

export const OWN_PRESETS: readonly OwnPreset[] = [
  {
    id: 'loop1',
    label: '1 重ループ',
    ops: 'N',
    input: { kind: 'n-only' },
    code: `let n = Int(readLine()!)!
var total = 0
for i in 0..<n {
    viz.tick()
    total += i % 7
}
print(total)
`,
  },
  {
    id: 'loop2',
    label: '2 重ループ',
    ops: 'N^2',
    input: { kind: 'n-only' },
    code: `let n = Int(readLine()!)!
var total = 0
for i in 0..<n {
    for j in 0..<n {
        viz.tick()
        total += (i ^ j) & 1
    }
}
print(total)
`,
  },
  {
    id: 'halving',
    label: 'N 回 × 半分にし続ける',
    ops: 'N*log2(N)',
    input: { kind: 'n-only' },
    code: `let n = Int(readLine()!)!
var total = 0
for _ in 0..<n {
    var m = n
    while m > 1 {
        viz.tick()
        m /= 2
    }
    total += 1
}
print(total)
`,
  },
  {
    id: 'array',
    label: '配列を読む 1 重ループ',
    ops: 'N',
    input: { kind: 'n-and-array', valueMin: 1, valueMax: 999 },
    code: `let n = Int(readLine()!)!
let a = readLine()!.split(separator: " ").map { Int($0)! }
var total = 0
for v in a {
    viz.tick()
    total += v
}
print(n, total)
`,
  },
];

/** サイズの候補（1 リクエスト最大 10 ケース）。 */
export const SIZE_CHOICES: readonly number[] = [
  10, 100, 1_000, 10_000, 20_000, 50_000, 100_000, 200_000, 500_000, 1_000_000,
];

export const DEFAULT_OWN_SIZES: readonly number[] = DEFAULT_SIZES;

export function presetById(id: string): OwnPreset | undefined {
  return OWN_PRESETS.find((p) => p.id === id);
}

/** 選んだサイズを 1〜10 件の昇順に整える。0 件なら null。 */
export function normalizeSizes(sizes: Iterable<number>): number[] | null {
  const out = [...new Set(sizes)]
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 1_000_000)
    .sort((a, b) => a - b);
  if (out.length === 0) return null;
  return out.slice(0, MAX_CASES);
}

/** 画面の設定から meter 指定を作る。ops が空なら間引かない（§5.3）。 */
export function buildOwnMeter(input: MeterInput, sizes: readonly number[], ops: string): MeterSpec {
  const trimmed = ops.trim();
  return trimmed === '' ? { input, sizes: [...sizes] } : { input, sizes: [...sizes], ops: trimmed };
}

/**
 * 回数の式を検証する（§5.1）。空なら間引かないので null。構文エラーや N 以外の識別子は
 * 計測前に文言として返す（planMeasurement の evalOps で OpsError を投げさせない）。
 */
export function validateOwnOps(ops: string): string | null {
  const trimmed = ops.trim();
  if (trimmed === '') return null;
  let names: string[];
  try {
    names = opsIdentifiers(trimmed);
  } catch (e) {
    if (e instanceof OpsError)
      return '回数の式を読み取れませんでした。空にすると間引かずに送ります。';
    throw e;
  }
  const unknown = names.filter((n) => n !== 'N');
  if (unknown.length > 0)
    return `回数の式に使える文字は N だけです（${unknown.join('、')} は使えません）。空にすると間引かずに送ります。`;
  return null;
}
